import React from 'react';
import { MapPin, Phone, ShieldCheck, Crosshair, Map, Store, Users, Target, ArrowRight, CheckCircle2, Calendar, Info, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL, ADDRESS_LINE_1, ADDRESS_LINE_2, FACEBOOK_URL } from '../constants/business';
import { brandGroups } from '../constants/brands';
import { BrandLogoCard } from '../components/BrandLogoCard';
export function Home() {
  useDocumentMeta('Kjergaard Sports | Firearms, Hunting Gear & Sporting Goods in Lake Benton, Minnesota', 'Visit Kjergaard Sports in Lake Benton, Minnesota for a large in-store selection of firearms, ammunition, optics, reloading supplies, safes, hunting blinds, accessories, classes, events, and shooting sports support.');
  const features = [{
    icon: <Store className="w-8 h-8 text-brand-hunter" data-id="element-727" />,
    title: 'A Huge In-Store Selection',
    description: 'Browse thousands of firearms and a massive inventory of gear in person. We stock the volume of a big-box store with the atmosphere of a local outfitter.'
  }, {
    icon: <Users className="w-8 h-8 text-brand-hunter" data-id="element-728" />,
    title: 'Practical Product Knowledge',
    description: 'Get guidance from staff who actually use the equipment. We help you find the right tool for the field, the range, or personal defense.'
  }, {
    icon: <Target className="w-8 h-8 text-brand-hunter" data-id="element-729" />,
    title: 'Built for Hunters, Shooters & Collectors',
    description: 'From upland bird hunting to precision reloading, our inventory is curated for serious outdoorsmen and shooting sports enthusiasts.'
  }];
  const heroPoints = [{
    icon: <Store className="w-5 h-5 text-brand-leather" data-id="element-730" />,
    text: '5,000+ guns on hand'
  }, {
    icon: <ShieldCheck className="w-5 h-5 text-brand-leather" data-id="element-731" />,
    text: 'In-store service only'
  }, {
    icon: <Crosshair className="w-5 h-5 text-brand-leather" data-id="element-732" />,
    text: 'Hunting, shooting sports, CCW & collectors'
  }, {
    icon: <MapPin className="w-5 h-5 text-brand-leather" data-id="element-733" />,
    text: 'Lake Benton, Minnesota'
  }];
  const categories = [{
    title: 'Firearms',
    image: 'https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg',
    alt: 'Firearms selection at Kjergaard Sports',
    description: 'Approximately 5,000 new and used firearms on hand, including options for hunting, sport shooting, personal defense, collecting, trap, skeet, waterfowl, upland, varmints, and more.',
    link: '/firearms'
  }, {
    title: 'Ammunition',
    image: 'https://cdn.magicpatterns.com/uploads/w1nfdCex88kVGKJTVUQepJ/ammunition-wall.jpg',
    alt: 'Ammunition and reloading supplies at Kjergaard Sports',
    description: 'A large in-store ammunition selection for hunting, target shooting, trap, skeet, waterfowl, upland, turkey, big game, personal defense, and popular calibers.',
    link: '/ammunition'
  }, {
    title: 'Optics',
    image: 'https://cdn.magicpatterns.com/uploads/1zvBEqUZKRM4BZCWhxgmNi/optics-cases.jpg',
    alt: 'Scopes and optics available at Kjergaard Sports',
    description: 'Scopes, binoculars, spotting scopes, rangefinders, and optics options for hunters, target shooters, and outdoor use.',
    link: '/optics'
  }, {
    title: 'Reloading Supplies',
    image: 'https://cdn.magicpatterns.com/uploads/8918DCMwwSgbp8VrUA2ZyG/reloading-supplies.jpg',
    alt: 'Reloading supplies at Kjergaard Sports',
    description: 'Bullets, powders, primers, presses, dies, scales, tools, and reloading support for customers who enjoy building safe, accurate loads.',
    link: '/reloading-supplies'
  }, {
    title: 'Safes & Accessories',
    image: 'https://cdn.magicpatterns.com/uploads/esrRsJE1XbS5zcQi7PgR9E/safes-display.jpg',
    alt: 'Gun safes and secure storage at Kjergaard Sports',
    description: 'Safes, cleaning supplies, cases, holsters, magazines, rests, targets, clay birds, firearm maintenance items, and shooting accessories.',
    link: '/safes-accessories'
  }, {
    title: 'Hunting Gear & Outdoor Gear',
    image: 'https://cdn.magicpatterns.com/uploads/916kAQgnnbRYFKKSKvhkwD/hunting-apparel-gear.jpg',
    alt: 'Hunting apparel and outdoor gear at Kjergaard Sports',
    description: 'Apparel, boots, packs, blinds, decoys, calls, and outdoor gear for customers getting ready for the field — bird, big game, waterfowl, and turkey.',
    link: '/hunting-gear-blinds'
  }];
  const services = ['Gunsmithing — barrel threading, scope mounting, repair & stock work', 'Suppressors & NFA / Form 4 help with electronic fingerprints in-store', 'Firearm transfers — inbound, outbound & out-of-state logistics', 'Used guns, appraisals, collections & estate buying', 'Minnesota Permit to Carry classes with Lance', 'Banquet & fundraiser firearm support for local groups'];
  const events = [{
    title: 'Minnesota Permit to Carry Classes',
    description: '$100, no signup needed, held on Saturdays. Lance is your contact — call or email for the next class date.',
    cta: 'Class details',
    link: '/mn-permit-to-carry-classes'
  }, {
    title: 'Shotgun Demo Day',
    description: 'July 18, 9:00 AM – 5:00 PM. Compare shotguns in person and meet with manufacturer reps. Follow Facebook for the latest details.',
    cta: 'Events page',
    link: '/events'
  }, {
    title: 'Gunsmithing & Barrel Threading',
    description: 'Recently expanded with a high-end mill and lathe. Nick’s barrel threading work is a core in-house capability.',
    cta: 'Gunsmithing',
    link: '/gunsmithing'
  }];
  const homeServiceCards = [{
    title: 'Suppressors & NFA Process',
    description: 'In-store ATF Form 4 help and electronic fingerprints. Set up your eForms account before you come in.',
    link: '/nfa-process'
  }, {
    title: 'Firearm Transfers',
    description: 'Inbound transfers $35, NFA transfers $100. Outbound shipping handled daily.',
    link: '/firearm-transfers'
  }, {
    title: 'Used Guns, Collections & Estates',
    description: 'One gun or a full estate — we’ll see it in person. Appraisal fee waived if we make a deal.',
    link: '/used-guns-appraisals-estates'
  }, {
    title: 'Banquets & Fundraisers',
    description: 'Sourcing fundraising firearms and managing winner transfers for small events and large national organizations.',
    link: '/banquets-fundraisers'
  }];
  // brandGroups now imported from constants/brands.ts for shared logo data
  const reviews = [{
    text: 'Very friendly and the staff has a lot of knowledge. Prices are very reasonable.',
    author: 'Marty Hilfers'
  }, {
    text: 'Huge inventory with a nice store. A little out of the way, but well worth the trip.',
    author: 'Jacob Raml'
  }, {
    text: 'Fantastic experience today. Extremely friendly and helpful. Great prices too! Worth the gas to get there!',
    author: 'J D'
  }];
  return <div data-id="element-734">
      {/* HERO */}
      <section className="relative flex items-center min-h-[78vh] lg:min-h-[680px] pt-12 lg:pt-16 pb-12 lg:pb-16" data-id="element-735">
        {/* Background image */}
        <div className="absolute inset-0 z-0" data-id="element-736">
          <img src="https://cdn.magicpatterns.com/uploads/6KbpNKGWUu4crVHhGcrQiK/home-hero-store-interior.jpg" alt="Inside Kjergaard Sports — a large rural sporting goods and firearms store in Lake Benton, Minnesota" className="w-full h-full object-cover object-center" data-id="element-737" />
          {/* Left-to-right gradient: dark behind text, lighter toward the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/55 to-brand-charcoal/15" data-id="element-738"></div>
          {/* Subtle bottom fade for section transition */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-brand-charcoal/70 to-transparent" data-id="element-739"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-740">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center" data-id="element-741">
            {/* LEFT: Hero content */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="lg:col-span-7" data-id="element-742">
              <div className="flex items-center space-x-3 mb-5" data-id="element-743">
                <div className="h-px w-10 bg-brand-leather" data-id="element-744"></div>
                <span className="text-brand-bone uppercase tracking-widest text-xs sm:text-sm font-semibold drop-shadow-md" data-id="element-745">
                  Lake Benton, Minnesota
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif text-brand-offwhite mb-4 leading-[1.05] drop-shadow-lg" data-id="element-746">
                Kjergaard Sports
              </h1>

              <p className="text-xl md:text-2xl font-serif italic text-brand-leather mb-6 drop-shadow-md" data-id="element-747">
                Where the service and price are worth the trip.
              </p>

              <p className="text-base md:text-lg text-brand-cream mb-9 leading-relaxed max-w-xl drop-shadow-md" data-id="element-748">
                A rural shooting sports and hunting supply destination with
                approximately 5,000 guns on hand, plus ammunition, optics,
                reloading supplies, safes, hunting gear, accessories, and
                in-store expertise.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6" data-id="element-749">
                <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base" data-id="element-750">
                  Plan Your Visit
                </Link>
                <a href={PHONE_TEL} className="border-2 border-brand-bone hover:border-brand-leather hover:bg-brand-leather/10 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base flex items-center justify-center" data-id="element-751">
                  <Phone className="w-5 h-5 mr-2" data-id="element-752" />
                  Call {PHONE}
                </a>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-offwhite/10 hover:bg-brand-offwhite/20 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base flex items-center justify-center backdrop-blur-sm border border-brand-offwhite/20" data-id="element-753">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-754" />
                  Get Directions
                </a>
              </div>

              <p className="text-brand-bone/80 text-xs leading-relaxed max-w-xl drop-shadow-md" data-id="element-755">
                This website is for business information only. Kjergaard Sports
                does not conduct sales directly through this website. All
                firearm-related sales, transfers, and regulated services are
                handled in-store and in accordance with applicable laws.
              </p>
            </motion.div>

            {/* RIGHT: "Why People Make the Drive" card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.15
          }} className="lg:col-span-5" data-id="element-756">
              <div className="bg-brand-offwhite/95 backdrop-blur-md border border-brand-bone shadow-2xl rounded-sm p-7 md:p-8 lg:ml-auto lg:max-w-md" data-id="element-757">
                <div className="flex items-center space-x-3 mb-4" data-id="element-758">
                  <div className="h-px w-8 bg-brand-leather" data-id="element-759"></div>
                  <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-760">
                    A Destination Store
                  </span>
                </div>

                <h2 className="text-2xl md:text-3xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-761">
                  Why People Make the Drive
                </h2>

                <ul className="space-y-4 mb-7" data-id="element-762">
                  {heroPoints.map((point, index) => <li key={index} className="flex items-start" data-id="element-763">
                      <span className="w-9 h-9 bg-brand-bone rounded-full flex items-center justify-center mr-3 flex-shrink-0" data-id="element-764">
                        {point.icon}
                      </span>
                      <span className="text-brand-charcoal font-medium pt-1.5 leading-snug" data-id="element-765">
                        {point.text}
                      </span>
                    </li>)}
                </ul>

                <div className="pt-5 border-t border-brand-bone" data-id="element-766">
                  <div className="flex items-start mb-3" data-id="element-767">
                    <MapPin className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-768" />
                    <div data-id="element-769">
                      <p className="text-brand-charcoal/85 text-sm leading-snug" data-id="element-770">
                        {ADDRESS_LINE_1}
                        <br data-id="element-771" />
                        {ADDRESS_LINE_2}
                      </p>
                    </div>
                  </div>
                  <a href={PHONE_TEL} className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-772">
                    <Phone className="w-4 h-4 mr-2" data-id="element-773" />
                    {PHONE}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-brand-offwhite bg-texture" data-id="element-774">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-775">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-776">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-777">
              Worth the Trip Since the First Visit
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-778">
              Kjergaard Sports is not a small generic shop. It is an enormous
              regional destination store with a deep in-store selection,
              knowledgeable service, and a practical hunting and shooting sports
              focus. Many customers travel long distances because they can
              browse a large selection in person and get guidance from people
              who understand the products.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-779">
            {features.map((feature, index) => <div key={index} className="bg-brand-cream p-8 rounded-sm border border-brand-bone shadow-sm hover:shadow-md transition-shadow" data-id="element-780">
                <div className="w-16 h-16 bg-brand-bone rounded-full flex items-center justify-center mb-6" data-id="element-781">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-782">
                  {feature.title}
                </h3>
                <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-783">
                  {feature.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="selection" className="py-20 bg-brand-bone" data-id="element-784">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-785">
          <div className="mb-12" data-id="element-786">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-787">
              What You'll Find In Store
            </h2>
            <div className="h-1 w-20 bg-brand-leather" data-id="element-788"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-789">
            {categories.map((category, index) => <div key={index} className="bg-brand-offwhite rounded-sm overflow-hidden group border border-brand-cream shadow-sm hover:shadow-md transition-all" data-id="element-790">
                <div className="relative h-64 overflow-hidden" data-id="element-791">
                  <img src={category.image} alt={category.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-792" />
                  <div className="absolute inset-0 bg-brand-charcoal/10 group-hover:bg-transparent transition-colors" data-id="element-793"></div>
                </div>
                <div className="p-6" data-id="element-794">
                  <h3 className="text-2xl font-serif text-brand-charcoal mb-3" data-id="element-795">
                    {category.title}
                  </h3>
                  <p className="text-brand-charcoal/75 mb-6 line-clamp-3" data-id="element-796">
                    {category.description}
                  </p>
                  <Link to={category.link} className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-797">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" data-id="element-798" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* STORE TOUR GALLERY — bigger, asymmetric collage */}
      <section className="py-20 bg-brand-offwhite" data-id="element-799">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-800">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12" data-id="element-801">
            <div className="lg:col-span-7" data-id="element-802">
              <h2 className="text-3xl md:text-5xl font-serif text-brand-charcoal mb-4 leading-tight" data-id="element-803">
                A Store Worth the Drive
              </h2>
              <div className="h-1 w-20 bg-brand-leather mb-6" data-id="element-804"></div>
              <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-805">
                Approximately 5,000 firearms on hand. Racks of rifles and
                shotguns. Walls of ammunition and reloading components. A
                workshop built for real in-person help. This is what a serious
                Minnesota sporting goods destination looks like — take a look
                around before you make the trip.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end" data-id="element-806">
              <Link to="/store-tour" className="inline-flex items-center bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-6 py-3 rounded-sm font-medium transition-colors" data-id="element-807">
                Take the Full Store Tour
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-808" />
              </Link>
            </div>
          </div>

          {/* Asymmetric photo collage */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6" data-id="element-809">
            {/* Large featured image */}
            <div className="lg:col-span-8 overflow-hidden rounded-sm shadow-md group" data-id="element-810">
              <img src="https://cdn.magicpatterns.com/uploads/4PEc13YMi3QHpCzosrhrkQ/store-interior-firearms-wide.jpg" alt="Wide view of the firearms floor at Kjergaard Sports — racks of rifles and shotguns" className="w-full h-72 md:h-[28rem] lg:h-[32rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-811" />
            </div>

            {/* Right side stack */}
            <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6" data-id="element-812">
              <div className="overflow-hidden rounded-sm shadow-md group" data-id="element-813">
                <img src="https://cdn.magicpatterns.com/uploads/1BGu7nrTdE3WRCeMKF6WrW/store-tour-wide.jpg" alt="Main floor and aisles at Kjergaard Sports" className="w-full h-44 md:h-56 lg:h-[15.5rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-814" />
              </div>
              <div className="overflow-hidden rounded-sm shadow-md group" data-id="element-815">
                <img src="https://cdn.magicpatterns.com/uploads/kENmASH4b2qj4AE9LqDfT7/mounted-rural-detail.jpg" alt="Mounted game and rural atmosphere at Kjergaard Sports" className="w-full h-44 md:h-56 lg:h-[15.5rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-816" />
              </div>
            </div>
          </div>

          {/* Secondary supporting row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-4 md:mt-6" data-id="element-817">
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-818">
              <img src="https://cdn.magicpatterns.com/uploads/w1nfdCex88kVGKJTVUQepJ/ammunition-wall.jpg" alt="Walls of in-store ammunition at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-819" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-820">
              <img src="https://cdn.magicpatterns.com/uploads/8918DCMwwSgbp8VrUA2ZyG/reloading-supplies.jpg" alt="Reloading components and supplies at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-821" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-822">
              <img src="https://cdn.magicpatterns.com/uploads/esrRsJE1XbS5zcQi7PgR9E/safes-display.jpg" alt="Gun safes and secure storage at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-823" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-824">
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="In-store gunsmithing workshop at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-825" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 bg-brand-cream" data-id="element-826">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-827">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-828">
            <div className="order-2 lg:order-1" data-id="element-829">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-830">
                A Store Built Around Real In-Person Help
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-831">
                Kjergaard Sports is a practical, knowledgeable, local store
                where customers can ask questions, compare products in person,
                and get help choosing the right equipment. We take pride in
                offering humble, small-town service—not a corporate retail
                experience.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-832">
                {services.map((service, index) => <li key={index} className="flex items-start" data-id="element-833">
                    <CheckCircle2 className="w-6 h-6 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-834" />
                    <span className="text-brand-charcoal font-medium" data-id="element-835">
                      {service}
                    </span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-836">
                <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-837">
                  Ask a Question
                </Link>
                <a href={PHONE_TEL} className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-838">
                  Call the Store
                </a>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative" data-id="element-839">
              <div className="absolute inset-0 bg-brand-sage translate-x-4 translate-y-4 rounded-sm" data-id="element-840"></div>
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Gunsmithing workshop and in-store support at Kjergaard Sports" className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-[4/3]" data-id="element-841" />
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="py-20 bg-brand-hunter text-brand-offwhite relative overflow-hidden" data-id="element-842">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-843">
          <img src="https://cdn.magicpatterns.com/uploads/4W49omroAHfAdaTefJNmcK/targets-shooting-supplies.jpg" alt="Reactive steel targets and shooting supplies at Kjergaard Sports" className="w-full h-full object-cover" data-id="element-844" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-845">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-846">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-847">
              Classes, Events &amp; Store Updates
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-848"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-849">
            {events.map((event, index) => <div key={index} className="bg-brand-charcoal/40 backdrop-blur-sm border border-brand-sage/30 p-8 rounded-sm flex flex-col" data-id="element-850">
                <Calendar className="w-8 h-8 text-brand-leather mb-4" data-id="element-851" />
                <h3 className="text-xl font-serif mb-3" data-id="element-852">{event.title}</h3>
                <p className="text-brand-offwhite/80 mb-6 flex-grow" data-id="element-853">
                  {event.description}
                </p>
                <div className="flex flex-col space-y-3 pt-2" data-id="element-854">
                  <Link to={event.link} className="text-sm font-medium text-brand-leather hover:text-brand-bone transition-colors" data-id="element-855">
                    {event.cta} →
                  </Link>
                  <a href={PHONE_TEL} className="text-sm text-brand-offwhite/70 hover:text-brand-bone transition-colors" data-id="element-856">
                    Or call {PHONE}
                  </a>
                </div>
              </div>)}
          </div>

          {/* Second-row service strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 max-w-6xl mx-auto" data-id="element-857">
            {homeServiceCards.map((card, index) => <Link key={index} to={card.link} className="group bg-brand-offwhite/5 hover:bg-brand-offwhite/10 backdrop-blur-sm border border-brand-sage/30 hover:border-brand-leather/50 p-5 rounded-sm transition-colors" data-id="element-858">
                <h4 className="text-base font-serif text-brand-offwhite mb-2" data-id="element-859">
                  {card.title}
                </h4>
                <p className="text-brand-offwhite/75 text-sm leading-relaxed mb-3" data-id="element-860">
                  {card.description}
                </p>
                <span className="inline-flex items-center text-brand-leather text-sm font-medium group-hover:text-brand-bone transition-colors" data-id="element-861">
                  Learn more
                  <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" data-id="element-862" />
                </span>
              </Link>)}
          </div>

          <div className="bg-brand-charcoal/60 backdrop-blur-md border border-brand-sage/50 p-6 rounded-sm flex items-start max-w-4xl mx-auto" data-id="element-863">
            <Info className="w-6 h-6 text-brand-leather mr-4 flex-shrink-0 mt-1" data-id="element-864" />
            <p className="text-brand-offwhite/90 text-lg" data-id="element-865">
              Class dates and event details change. Call the store or follow
              Kjergaard Sports on Facebook for current updates.
            </p>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="py-20 bg-brand-offwhite" data-id="element-866">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-867">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-868">
            <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-869">
              <div className="h-px w-10 bg-brand-leather" data-id="element-870"></div>
              <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-871">
                Brands We Work With
              </span>
              <div className="h-px w-10 bg-brand-leather" data-id="element-872"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-873">
              Major Brands, Real In-Store Knowledge
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-874">
              Kjergaard Sports works closely with many of the names hunters,
              shooters, and collectors already trust. Visit the store to compare
              options in person, ask questions, and get help finding the right
              fit.
            </p>
          </div>

          <div className="space-y-8 mb-10" data-id="element-875">
            {brandGroups.map(group => <div key={group.heading} data-id="element-876">
                <h3 className="text-sm font-semibold text-brand-hunter uppercase tracking-wider mb-4" data-id="element-877">
                  {group.heading}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-id="element-878">
                  {group.brands.map(brand => <BrandLogoCard key={brand.name} brand={brand} showLabel={false} size="sm" data-id="element-879" />)}
                </div>
              </div>)}
          </div>

          <div className="max-w-4xl mx-auto bg-brand-bone border-l-4 border-brand-leather p-6 rounded-sm" data-id="element-880">
            <p className="text-brand-charcoal/90 text-sm leading-relaxed" data-id="element-881">
              <strong data-id="element-882">Note:</strong> Benelli, Franchi, Stoeger, and Uberti are
              related brands under the same parent company. Beretta, Tikka, and
              Sako are also related brands — Kjergaard Sports is a{' '}
              <strong data-id="element-883">Sako Select dealer</strong>. Winchester appears as both
              firearms and ammunition. Kjergaard Sports was{' '}
              <strong data-id="element-884">Pulsar's #3 store in the country last year</strong>,
              giving customers access to deep product knowledge and real-world
              guidance on thermal optics.
            </p>
            <div className="mt-4" data-id="element-885">
              <Link to="/brands" className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors text-sm" data-id="element-886">
                See the full brands list
                <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-887" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS — larger, more readable quotes */}
      <section className="py-24 bg-brand-cream bg-texture" data-id="element-888">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-889">
          <div className="text-center mb-16" data-id="element-890">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-891">
              Why Customers Say It's Worth the Drive
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-892"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10" data-id="element-893">
            {reviews.map((review, index) => <div key={index} className="flex flex-col bg-brand-offwhite p-8 md:p-10 rounded-sm border border-brand-bone shadow-sm" data-id="element-894">
                <Quote className="w-10 h-10 text-brand-leather mb-5" data-id="element-895" />
                <blockquote className="flex-grow mb-7" data-id="element-896">
                  <p className="text-xl md:text-2xl font-serif text-brand-charcoal leading-snug" data-id="element-897">
                    &ldquo;{review.text}&rdquo;
                  </p>
                </blockquote>
                <div className="pt-5 border-t border-brand-wood/20" data-id="element-898">
                  <p className="font-semibold text-brand-hunter uppercase tracking-wider text-sm" data-id="element-899">
                    — {review.author}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PLAN YOUR VISIT */}
      <section className="py-20 bg-brand-bone" data-id="element-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-901">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-902">
            <div className="grid grid-cols-2 gap-4" data-id="element-903">
              <div className="col-span-2" data-id="element-904">
                <img src="https://cdn.magicpatterns.com/uploads/f5Jp1yjaV9t3nwDxVsC5wU/exterior-front-building.jpg" alt="Kjergaard Sports storefront in Lake Benton, Minnesota" className="w-full h-64 object-cover rounded-sm shadow-sm" data-id="element-905" />
              </div>
              <div data-id="element-906">
                <img src="https://cdn.magicpatterns.com/uploads/r78gFzpmsNjcdavFnWNwrr/property-sign.jpg" alt="Kjergaard Sports property sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-907" />
              </div>
              <div data-id="element-908">
                <img src="https://cdn.magicpatterns.com/uploads/pvSYjNceL5zo92xh1cTiRL/road-sign.jpg" alt="Kjergaard Sports road sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-909" />
              </div>
            </div>

            <div data-id="element-910">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-911">
                Plan Your Visit to Lake Benton
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-912">
                We are an out-of-town destination store, and we know our
                customers travel to see us. When you make the trip, you'll find
                a massive selection and staff ready to help.
              </p>

              <div className="space-y-6 mb-10" data-id="element-913">
                <div className="flex items-start" data-id="element-914">
                  <MapPin className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-915" />
                  <div data-id="element-916">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-917">
                      Address
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-918">
                      {ADDRESS_LINE_1}
                      <br data-id="element-919" />
                      {ADDRESS_LINE_2}
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-920">
                  <Phone className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-921" />
                  <div data-id="element-922">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-923">
                      Phone
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-924">{PHONE}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-925">
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-926">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-927" />
                  Get Directions
                </a>
                <a href={PHONE_TEL} className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-928">
                  <Phone className="w-5 h-5 mr-2" data-id="element-929" />
                  Call {PHONE}
                </a>
                <Link to="/contact" className="bg-brand-offwhite hover:bg-brand-cream text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors border border-brand-cream" data-id="element-930">
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}